import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import Sheet from "components/sheet/Sheet";
import React, { useState } from "react";
import { showError, showSuccess } from "redux/actions/ui";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { ButtonNew as Button } from "components/buttonNew";

import { useAppDispatch } from "hooks/redux";
import "./rainCheckCompletePopup.scss";
import { LocaleCurrency } from "helpers/Locale";
import { EmailRainCheckReceipt, GetRainCheckReceipt } from "api/rpc/2024-04/facilityAdmin/payment/rainCheck";

interface IRainCheck {
  id: number;
  code: string;
  last4: string;
  balance: number;
  currency: string;
  facility_id: number;
  client_id: number;
  customer_id: number;
  status: string;
  order_id: number;
  note: string;
  user_id: number;
  tee_time_booking_participant_id: number;
  updated_at: Date;
  created_at: Date;
}

interface IProps {
  /**Boolean that determines if the action bar is displayed */
  open: boolean;
  /**Function that sets the state of the open boolean */
  setClose: (open: boolean) => void;
  /**Current active order */
  rainCheck: Record<string, any> | IRainCheck;

  //Raincheck Customer
  bookingParticipant: any;

  rainCheckState: Record<string, any>;
}

interface IEmailState {
  modalVisible: boolean;
  email: string;
}

export default function RainCheckCompletePopup(props: IProps) {
  const { t } = useTranslation();
  const { open, setClose, rainCheck, bookingParticipant } = props;
  const dispatch = useAppDispatch();

  const [emailState, setEmailState] = useState<IEmailState>({
    modalVisible: false,
    email: "",
  });

  async function printReceipt() {
    if (rainCheck) {
      const rainCheckReceiptRes = await GetRainCheckReceipt({ id: rainCheck?.id }, true);
      if (rainCheckReceiptRes.status !== StatusCode.OK) {
        dispatch(showError(rainCheckReceiptRes.message));
        return;
      } else {
        window.open().document.write(rainCheckReceiptRes.data as string);
      }
    }
  }

  async function emailReceipt() {
    const emailReceiptRes = await EmailRainCheckReceipt(
      { id: rainCheck?.id, customer_email: emailState?.email },
      true,
    );
    if (emailReceiptRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending email rain check")));
      return;
    }
    setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }));
    dispatch(showSuccess(t("Email rain check sent successfully")));
  }

  function handleEmailInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setEmailState(prevState => ({ ...prevState, [id]: value }));
  }

  return (
    <>
      <Popup
        type="success"
        open={open}
        title={t("Rain check issued")}
        description={t("A rain check has been issued for Order") + ` # ${rainCheck?.order_id}`}
        closable
        onOk={setClose}
        okText={t("Close")}
        backDropCancel={false}
      >


        <div className="ui-rain-check-popup">
            <div className="ui-rain-check-popup-top">
                <p className="ui-rain-check-popup-title">{t("Item Summary")}</p>
                <div className="ui-rain-check-popup-item-container">
                  <div className="flex gap-2">
                    <p className="ui-rain-check-popup-product-title">1</p>
                    <div>
                      <p className="ui-rain-check-popup-product-title">{bookingParticipant?.green_fee_product_title}</p>
                      {bookingParticipant?.green_fee_variant_title !== bookingParticipant?.green_fee_product_title ? (
                        <p className="ui-rain-check-popup-variant-title">{bookingParticipant?.green_fee_variant_title}</p>
                      ) : null}
                    </div>
                  </div>
                    <LocaleCurrency amount={bookingParticipant?.green_fee_price} currency={rainCheck?.currency ?? "CAD"} />
                </div>
                {props.rainCheckState.issue_on_power_cart ? (<div className="ui-rain-check-popup-item-container">
                  <div className="flex gap-2">
                    <p className="ui-rain-check-popup-product-title">1</p>
                    <div>
                      <p className="ui-rain-check-popup-product-title">{bookingParticipant?.power_cart_product_title}</p>
                      {bookingParticipant?.power_cart_variant_title !== bookingParticipant?.power_cart_product_title ? (
                        <p className="ui-rain-check-popup-variant-title">{bookingParticipant?.power_cart_variant_title}</p>
                      ) : null}
                    </div>
                  </div>
                    <LocaleCurrency amount={bookingParticipant?.power_cart_price} currency={rainCheck?.currency ?? "CAD"} />
                </div>) : null}
            </div>
            <div className="ui-rain-check-popup-footer">
                <p className="ui-rain-check-popup-title">{t("Rain check amount")}</p>
                <LocaleCurrency style={{color: "#039855"}} amount={rainCheck?.balance} currency={rainCheck?.currency ?? "CAD"} />
            </div>
        </div>


        <div className="mt-4">
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Button block size="medium" onClick={printReceipt}>
                  {t('Print')}
                </Button>
                <Button
                  block
                  size="medium"
                  onClick={() =>
                    setEmailState(prevState => ({
                      ...prevState,
                      modalVisible: true,
                      email: bookingParticipant?.customer?.email,
                    }))
                  }
                >
                  {t('Email')}
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </div>
      </Popup>

      <Sheet
        title={t("Enter Email")}
        open={emailState.modalVisible}
        size="small"
        stacked
        closable
        onCancel={() => setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }))}
        onOk={emailReceipt}
        okText={t("Send Email")}
        okDisabled={!emailState.email ? true : false}
      >
        <Input placeholder={t("Email")} value={emailState.email} id="email" onChange={handleEmailInputChange} />
      </Sheet>
    </>
  );
}
